import { FileUpload } from '@consigli/facade';
import {
  ExtendedFile,
  useGetPackagesByTypeQuery,
  useProjectId,
  useServiceName,
} from '@consigli/hooks';
import { DocumentPackageType, PipelineName, WaterProcessFileType } from '@consigli/types';
import { useCallback, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ServiceIdIndexValue } from '@/routes';

import { IterationName } from '../components/iteration-name';
import { SelectLanguage } from '../components/select-language';
import { WaterProcessSystemsAgentUploadInfo } from '../components/upload-info-panel';
import { useFetchValidateName } from '../components/user-input';
import { WizardAnalyzeButton } from '../components/wizard-analyze-button';
import { TabType, WizardLayout } from '../components/wizard-layout';
import { useCreateInsightPackage } from '../hooks/use-create-insight-package';
import { useUploadProgress } from '../hooks/use-upload-progress';

export const WaterProcessSystemsAgentWizard = () => {
  const [formData] = useState<FieldValues>({
    iterationName: '',
    language: '',
  });

  const { t } = useTranslation();
  const [scheduleFiles, setScheduleFiles] = useState<ExtendedFile[]>([]);
  const [pidFiles, setPidFiles] = useState<ExtendedFile[]>([]);
  const [specificationFiles, setSpecificationFiles] = useState<ExtendedFile[]>([]);
  const projectId = useProjectId();
  const serviceName = useServiceName();
  const type = ServiceIdIndexValue[serviceName] as DocumentPackageType;
  const [isProcessing, setIsProcessing] = useState(false);
  const [targetCounter, setTargetCounter] = useState(0);
  const [currentCounter, setCurrentCounter] = useState(0);

  const { createDocumentPackage, uploadFiles, startAnalysis } = useCreateInsightPackage({
    setCurrentCounter,
    setTargetCounter,
  });

  const { isUploading, progress } = useUploadProgress(currentCounter, targetCounter);

  const methods = useForm<FieldValues>({
    defaultValues: formData,
  });

  const { getValues, handleSubmit } = methods;

  const onAnalyze = useCallback(async () => {
    try {
      setIsProcessing(true);

      const createdPackageId = await createDocumentPackage(
        type,
        getValues('iterationName'),
        getValues('language'),
        false,
      );

      if (createdPackageId === undefined) {
        return;
      }

      const tagFiles = (files: ExtendedFile[], fileType: WaterProcessFileType): ExtendedFile[] =>
        files.map((file) => {
          file.waterProcessFileType = fileType;
          return file;
        });

      const combinedFiles: ExtendedFile[] = [
        ...tagFiles(scheduleFiles, WaterProcessFileType.SCHEDULE),
        ...tagFiles(pidFiles, WaterProcessFileType.PID),
        ...tagFiles(specificationFiles, WaterProcessFileType.SPECS),
      ];

      await uploadFiles(combinedFiles, createdPackageId);
      await startAnalysis(createdPackageId, PipelineName.VALIDATION_TO_TEXT_PROCESSING);
      toast.success(t('package-creation.success-analyzed'));
    } catch (error) {
      console.log('Error starting analysis:', error);
      toast.error(t('package-creation.failed-analyzed'));
    } finally {
      setIsProcessing(false);
    }
  }, [
    getValues,
    createDocumentPackage,
    type,
    uploadFiles,
    startAnalysis,
    t,
    scheduleFiles,
    pidFiles,
    specificationFiles,
  ]);

  const { validateName } = useFetchValidateName(useGetPackagesByTypeQuery, {
    projectId,
    type,
  });

  const CreateWaterProcessPackageForm = () => {
    return (
      <>
        <FormProvider {...methods}>
          <form
            className="w-full max-w-[90rem] -mx-4 px-4 sm:mx-0 sm:px-0"
            onSubmit={handleSubmit(onAnalyze)}
          >
            <div className="flex flex-col max-w-[50rem] mx-auto" translate="no">
              <IterationName
                validateName={validateName}
                label={t('package-creation.analysis-name')}
                errorMessage={`${t('package-creation.analysis-name-empty')}*`}
              />
            </div>
            <div className="max-w-[50rem] mx-auto">
              <SelectLanguage />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
              <div className="flex flex-col">
                <p className="mb-2 text-base font-semibold text-day-neutral-subtle">
                  {t('package-creation.water-process-schedule-label')}
                </p>
                <FileUpload
                  files={scheduleFiles}
                  setFiles={setScheduleFiles}
                  dropAreaLabel={t('document-upload.drag-n-drop-here')}
                  dropAreaClassName="mt-2 h-48 w-full"
                  fileButtonLabel={t('document-upload.browse-files')}
                  folderButtonLabel={t('document-upload.browse-folders')}
                  uploadListLabel={t('package-creation.water-process-schedule-files')}
                  zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
                />
              </div>

              <div className="flex flex-col">
                <p className="mb-2 text-base font-semibold text-day-neutral-subtle">
                  {t('package-creation.water-process-pid-label')}
                </p>
                <FileUpload
                  files={pidFiles}
                  setFiles={setPidFiles}
                  dropAreaLabel={t('document-upload.drag-n-drop-here')}
                  dropAreaClassName="mt-2 h-48 w-full"
                  fileButtonLabel={t('document-upload.browse-files')}
                  folderButtonLabel={t('document-upload.browse-folders')}
                  uploadListLabel={t('package-creation.water-process-pid-files')}
                  zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
                />
              </div>
            </div>

            <div className="mt-6">
              <p className="mb-2 text-base font-semibold text-day-neutral-subtle">
                {t('package-creation.water-process-specification-label')}
              </p>
              <FileUpload
                files={specificationFiles}
                setFiles={setSpecificationFiles}
                dropAreaLabel={t('document-upload.drag-n-drop-here')}
                dropAreaClassName="mt-2 h-48 w-full"
                fileButtonLabel={t('document-upload.browse-files')}
                folderButtonLabel={t('document-upload.browse-folders')}
                uploadListLabel={t('package-creation.water-process-specification-files')}
                zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
              />
            </div>

            <div className="fc-w-full mt-6 max-w-[50rem] mx-auto">
              <WizardAnalyzeButton
                localFiles={[...scheduleFiles, ...pidFiles, ...specificationFiles]}
                type="submit"
              />
            </div>
          </form>
        </FormProvider>
      </>
    );
  };

  const messageHeader = <WaterProcessSystemsAgentUploadInfo />;

  return (
    <WizardLayout
      isProcessing={isProcessing}
      messageHeader={messageHeader}
      isUploading={isUploading}
      progress={progress}
      tabContent={{
        [TabType.CREATE_PACKAGE]: <CreateWaterProcessPackageForm />,
      }}
    />
  );
};
